import React, { useState, useEffect, useRef } from 'react';
import AceEditor from 'react-ace';
import { describeConfigError, validateCockpitConfig } from '../../utils/cockpitConfigValidator';
import { TAB_TYPES, slugify } from './cockpitConfig/tabTypes';
import TabList from './cockpitConfig/TabList';
import TabEditorPanel from './cockpitConfig/TabEditorPanel';

// Import ace modes and themes
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';

const cloneProperties = (properties) => {
  try {
    return structuredClone(properties);
  } catch (e) {
    return JSON.parse(JSON.stringify(properties ?? {}));
  }
};

const CockpitConfigTab = ({ config, onConfigChange, setConfigError, configError }) => {
  const [tabs, setTabs] = useState(() => (config && config.tabs ? config.tabs : []));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [configString, setConfigString] = useState(() =>
    JSON.stringify(config && config.tabs ? config : { tabs: [] }, null, 2)
  );
  const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  // Holds the last config object we emitted ourselves, so we can ignore its echo
  const selfEmittedConfig = useRef(null);
  const isJsonEditorActive = useRef(false);

  // Re-initialize tabs only when the config changes from an EXTERNAL source
  // (loading a cockpit, etc.) and NOT when it's the echo of our own emission.
  useEffect(() => {
    if (config === selfEmittedConfig.current) {
      return;
    }

    if (config && config.tabs) {
      setTabs(config.tabs);
      if (!isJsonEditorActive.current) {
        setConfigString(JSON.stringify(config, null, 2));
      }
    } else {
      setTabs([]);
      if (!isJsonEditorActive.current) {
        setConfigString(JSON.stringify({ tabs: [] }, null, 2));
      }
    }
  }, [config]);

  // Keep selectedIndex within bounds
  useEffect(() => {
    if (selectedIndex > tabs.length - 1) {
      setSelectedIndex(Math.max(0, tabs.length - 1));
    }
  }, [tabs.length, selectedIndex]);

  // Apply a new tabs array: update local state then validate and emit upward.
  const commitTabs = (newTabs) => {
    setTabs(newTabs);

    const newConfig = { ...config, tabs: newTabs };
    const validation = validateCockpitConfig(newConfig);
    if (!validation.valid) {
      setConfigError(validation.error);
    } else {
      setConfigError('');
      selfEmittedConfig.current = newConfig;
      onConfigChange(newConfig);
      setConfigString(JSON.stringify(newConfig, null, 2));
    }
  };

  const addTab = () => {
    const name = `Nouvel onglet ${tabs.length + 1}`;
    const newTab = {
      name,
      type: 'embed',
      slug: slugify(name),
      admin_only: false,
      group: null,
      properties: cloneProperties(TAB_TYPES.embed.defaultProperties)
    };
    const newTabs = [...tabs, newTab];
    commitTabs(newTabs);
    setSelectedIndex(newTabs.length - 1);
  };

  const updateTab = (index, updatedTab) => {
    const newTabs = [...tabs];
    newTabs[index] = updatedTab;
    commitTabs(newTabs);
  };

  const removeTab = (index) => {
    const newTabs = [...tabs];
    newTabs.splice(index, 1);
    commitTabs(newTabs);
    setSelectedIndex((prev) => {
      if (index < prev) return prev - 1;
      if (index === prev) return Math.max(0, Math.min(prev, newTabs.length - 1));
      return prev;
    });
  };

  const reorderTabs = (fromIndex, toIndex) => {
    const newTabs = [...tabs];
    const [moved] = newTabs.splice(fromIndex, 1);
    newTabs.splice(toIndex, 0, moved);
    commitTabs(newTabs);
    setSelectedIndex(toIndex);
  };

  const handleJsonChange = (newValue) => {
    isJsonEditorActive.current = true;
    setConfigString(newValue);
    try {
      const parsedConfig = JSON.parse(newValue);
      const validation = validateCockpitConfig(parsedConfig);

      if (!validation.valid) {
        setConfigError(validation.error);
      } else {
        setConfigError('');
        selfEmittedConfig.current = parsedConfig;
        onConfigChange(parsedConfig);
        setTabs(parsedConfig.tabs || []);
      }
    } catch (err) {
      setConfigError('Format JSON invalide');
    }
  };

  const toggleJsonEditor = () => {
    isJsonEditorActive.current = !showJsonEditor;
    setShowJsonEditor(!showJsonEditor);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Configuration des onglets
        </h3>
        <button
          type="button"
          onClick={toggleJsonEditor}
          className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          {showJsonEditor ? 'Éditeur visuel' : 'Mode Expert'}
        </button>
      </div>

      {configError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-sm text-red-600 dark:text-red-400">{describeConfigError(configError, tabs)}</p>
        </div>
      )}

      {showJsonEditor ? (
        <div className="relative">
          <AceEditor
            mode="json"
            theme={isDarkMode ? 'monokai' : 'github'}
            onChange={handleJsonChange}
            value={configString}
            name="config-editor"
            editorProps={{ $blockScrolling: true }}
            setOptions={{
              useWorker: false,
              showLineNumbers: true,
              tabSize: 2,
            }}
            width="100%"
            height="400px"
            className={`border ${configError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md shadow-sm`}
          />
        </div>
      ) : tabs.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Aucun onglet configuré</p>
          <button
            type="button"
            onClick={addTab}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Ajouter un premier onglet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,18rem)_1fr] gap-4 min-h-[28rem]">
          <div className="md:border-r md:border-gray-200 md:dark:border-gray-700 md:pr-4">
            <TabList
              tabs={tabs}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
              onAdd={addTab}
              onReorder={reorderTabs}
            />
          </div>
          <div>
            <TabEditorPanel
              tab={tabs[selectedIndex]}
              index={selectedIndex}
              onUpdate={updateTab}
              onRemove={removeTab}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CockpitConfigTab;
